/* eslint-disable no-undef */
import React, {useCallback, useEffect, useState} from 'react';
import './CustomersTable.scss'
import {useRecoilState, useRecoilValue} from "recoil";
import {ADD, useToastContext} from "../Toast"
import {grpcCustomerServiceSelector} from "../../state/grpc";
import {
  Button,
  DataTable,
  DataTableSkeleton,
  OverflowMenu,
  OverflowMenuItem, Pagination,
  Table,
  TableBatchAction,
  TableBatchActions,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectAll,
  TableSelectRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
} from "@carbon/react";
import {RowDelete, Tools, Recording, RecordingFilled} from '@carbon/react/icons';
import {
  deleteModalCodeState,
  deleteModalOpenState,
  errorState,
  loadingState,
  rowsState,
  firstRowIndexState,
  pageSizeState
} from "../../state/customers_table";
import {useNavigate} from "react-router-dom";
import ConfirmDialog from "../ConfirmDialog";
import PropTypes from "prop-types";
import StopRecordingModal from "../StopRecordingModal";

const CustomersTable = ({runningRecording, updateRunningRecording}) => {
  const headers = [{
    key: 'name', header: 'Nome'
  }, {
    key: 'systemEmail', header: 'Email sistema'
  }, {
    key: 'serviceH24', header: 'H24'
  }, {
    key: 'warrantyExpiration', header: 'Scadenza garanzia'
  }, {
    key: 'systemType', header: 'Tipo sistema'
  }, {
    key: 'enabled', header: 'Attivo'
  }];

  const {toastDispatch} = useToastContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useRecoilState(loadingState)
  const [error, setError] = useRecoilState(errorState)
  const [rows, setRows] = useRecoilState(rowsState)
  const [deleteModalOpen, setDeleteModalOpen] = useRecoilState(deleteModalOpenState)
  const [deleteModalCode, setDeleteModalCode] = useRecoilState(deleteModalCodeState)
  const [firstRowIndex, setFirstRowIndex] = useRecoilState(firstRowIndexState)
  const [pageSize, setPageSize] = useRecoilState(pageSizeState)
  const customersService = useRecoilValue(grpcCustomerServiceSelector)

  const [startRecordingModalOpen, setStartRecordingModalOpen] = useState(false)
  const [stopRecordingModalOpen, setStopRecordingModalOpen] = useState(false)
  const [startRecordingRefId, setStartRecordingRefId] = useState("")

  const navigateToAdd = useCallback(() => {
    navigate('/customers/add')
  }, [navigate])

  const navigateToEdit = useCallback((customerCode) => {
    navigate('/customers/' + customerCode)
  }, [navigate])

  const navigateToDevices = useCallback((customerCode) => {
    navigate('/customers/' + customerCode + '/devices')
  }, [navigate])

  const enrichCustomersWithIds = (customers) => {
    return customers.map(c => {
      c.id = c.code;
      return c
    })
  }

  async function getCustomersTableData() {
    const req = new proto.api.ListCustomersRequest();
    customersService.listCustomers(req, null, (err, resp) => {
      if (err != null) {
        console.error(err)
        setError(true)
      } else {
        setRows(enrichCustomersWithIds(resp.toObject().customersList))
      }
      setLoading(false)
    })
  }

  useEffect(() => {
    getCustomersTableData();
    // eslint-disable-next-line
  }, [customersService, setError, setLoading, setRows]);

  if (loading) {
    return (
      <DataTableSkeleton
        columnCount={headers.length + 1}
        rowCount={5}
        headers={headers}
      />
    )
  }

  if (error) {
    return (<div>
      <span>ERROR!</span>
    </div>)
  }

  const processChildren = (cell) => {
    let value = cell.value
    switch (cell.info.header) {
      case "serviceH24": {
        value = cell.value ? "Si" : "No"
        break
      }
      case "systemType": {
        value = cell.value.length > 0 ? cell.value : "Rileva in automatico"
        break
      }
      case "enabled": {
        return (
          <div title={cell.value ? "Attivo" : "Disattivo"} className={cell.value ? "light green" : "light red"}/>
        )
      }
    }
    return (<>
      {value}
    </>)
  }

  const handlePaginationChange = (page, newPageSize) => {
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
    }
    setFirstRowIndex(newPageSize * (page - 1));
  }

  const systemMatchRunningRecording = (row) => {
    return runningRecording && (runningRecording.systemcode === row.id)
  }

  const startSystemRecording = (systemCode) => {
    const req = new proto.api.StartRecordingRequest()
    req.setSystemcode(systemCode)
    req.setRecordingnotes("")
    customersService.startRecording(req, null, (err, resp) => {
      if (err != null) {
        console.error(err)
        toastDispatch({
          type: ADD,
          payload: {
            title: "Operazione fallita",
            content: "Impossibile avviare la rendicontazione. Riprova più tardi",
            kind: "error",
            caption: err.message
          }
        })
        setStartRecordingModalOpen(false)
      } else {
        const recResp = resp.toObject().recording
        navigator.clipboard.writeText(recResp.systemlink)
          .then(r => {
            toastDispatch({
              type: ADD,
              payload: {
                title: "Operazione avvenuta con successo",
                content: `Rendicontazione avviata con successo. Il link del sistema è stato copiato sulla tua clipboard`,
                kind: "success"
              }
            })
          })
          .catch(err => {
            toastDispatch({
              type: ADD,
              payload: {
                title: "Operazione avvenuta con successo",
                content: `Rendicontazione avviata con successo`,
                kind: "success"
              }
            })
          })
          .finally(() => {
            updateRunningRecording(recResp)
            setStartRecordingModalOpen(false)
          })
      }
    })
  }

  return (
    <DataTable
      rows={rows}
      headers={headers}
      isSortable
      overflowMenuOnHover={false}
      render={({
                 rows,
                 headers,
                 getHeaderProps,
                 getRowProps,
                 getSelectionProps,
                 getToolbarProps,
                 getBatchActionProps,
                 onInputChange,
                 selectedRows,
                 getTableProps,
                 getTableContainerProps,
               }) => {
        return (
          <TableContainer title="Lista sistemi"
                          description="Lista dei sistemi registrati" {...getTableContainerProps()}>
            <ConfirmDialog
              openState={deleteModalOpen}
              setOpenState={setDeleteModalOpen}
              heading={"Sei sicuro di voler eliminare il sistema?"}
              primaryButtonText="Elimina"
              onRequestSubmit={() => {
                const req = new proto.api.DeleteCustomerRequest()
                req.setCode(deleteModalCode)
                customersService.deleteCustomer(req, null, (err, resp) => {
                  if (err != null) {
                    console.error(err)
                    toastDispatch({
                      type: ADD,
                      payload: {
                        title: "Operazione fallita",
                        content: "Impossibile eliminare il sistema",
                        kind: "error",
                        caption: err.message
                      }
                    })
                  } else {
                    getCustomersTableData()
                    setDeleteModalOpen(false)
                    toastDispatch({
                      type: ADD,
                      payload: {
                        title: "Operazione avvenuta con successo",
                        content: "Record sistema eliminato con successo",
                        kind: "success"
                      }
                    })
                  }
                })
              }}
            />
            <ConfirmDialog
              openState={startRecordingModalOpen}
              setOpenState={setStartRecordingModalOpen}
              heading={"Sei sicuro di voler avviare la rendicontazione?"}
              isDanger={false}
              primaryButtonText={"Avvia"}
              onRequestSubmit={() => {
                startSystemRecording(startRecordingRefId)
              }}
            />
            <StopRecordingModal
              isOpen={stopRecordingModalOpen}
              setOpen={setStopRecordingModalOpen}
              recording={runningRecording || {}}
              updateRecording={updateRunningRecording}
            />
            <TableToolbar {...getToolbarProps()} >
              <TableBatchActions {...getBatchActionProps()}>
                <TableBatchAction
                  renderIcon={RowDelete}
                  iconDescription="Elimina le righe selezionate"
                  onClick={() => console.log("batch action on: ", selectedRows.length, " rows")}>
                  Elimina
                </TableBatchAction>
              </TableBatchActions>
              <TableToolbarContent>
                <TableToolbarSearch onChange={onInputChange} size="lg"/>
                <Button onClick={navigateToAdd}>Nuovo sistema</Button>
              </TableToolbarContent>
            </TableToolbar>
            <Pagination
              totalItems={rows.length}
              backwardText="Precedente"
              forwardText="Successiva"
              pageSize={25}
              pageSizes={[10, 25, 50, 100]}
              itemsPerPageText="Elementi per pagina"
              pageRangeText={(_current, total) => `di ${total} ${total === 1 ? 'pagina' : 'pagine'}`}
              itemRangeText={(min, max, total) => `${min}–${max} of ${total} elementi`}
              onChange={({page, pageSize}) => handlePaginationChange(page, pageSize)}
            />
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  <TableSelectAll {...getSelectionProps()} />
                  {headers.map((header) => (<TableHeader {...getHeaderProps({header})}>
                    {header.header}
                  </TableHeader>))}
                  <TableHeader scope="col"/>
                  <TableHeader scope="col"/>
                  <TableHeader scope="col"/>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice(firstRowIndex, firstRowIndex + pageSize).map((row) => (
                  <TableRow key={row.id} {...getRowProps({row})}>
                    <TableSelectRow {...getSelectionProps({row})} />
                    {row.cells.map((cell) => (<TableCell key={cell.id}>
                      {processChildren(cell)}
                    </TableCell>))}
                    <TableCell className="cds--table-column-menu">
                      {
                        systemMatchRunningRecording(row) &&
                        <OverflowMenu
                          flipped size='md' aria-label="Ferma rendicontazione"
                          iconDescription="Ferma rendicontazione"
                          renderIcon={RecordingFilled}
                          onClick={() => {
                            setStopRecordingModalOpen(true)
                          }}
                        />
                      }
                      {
                        !systemMatchRunningRecording(row) &&
                        <OverflowMenu
                          flipped size='md' aria-label="Avvia rendicontazione"
                          iconDescription="Avvia rendicontazione"
                          renderIcon={Recording}
                          onClick={() => {
                            setStartRecordingRefId(row.id)
                            setStartRecordingModalOpen(true)
                          }}
                        />
                      }
                    </TableCell>
                    <TableCell className="cds--table-column-menu">
                      <OverflowMenu
                        flipped size='md' aria-label="Strumenti"
                        iconDescription="Strumenti"
                        renderIcon={Tools}
                        onClick={() => navigateToDevices(row.id)}/>
                    </TableCell>
                    <TableCell className="cds--table-column-menu">
                      <OverflowMenu flipped size='md' aria-label="Opzioni" iconDescription="Opzioni">
                        <OverflowMenuItem itemText="Modifica" onClick={() => navigateToEdit(row.id)}/>
                        <OverflowMenuItem isDelete itemText="Elimina"
                                          onClick={() => {
                                            setDeleteModalCode(row.id)
                                            setDeleteModalOpen(true)
                                          }}/>
                      </OverflowMenu>
                    </TableCell>
                  </TableRow>))}
              </TableBody>
            </Table>
          </TableContainer>);
      }}
    />
  )
}

CustomersTable.propTypes = {
  runningRecording: PropTypes.object,
  updateRunningRecording: PropTypes.func.isRequired
}

export default CustomersTable